import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { RefreshCw, ShieldAlert } from 'lucide-react';
import { adminAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { TotalsTable, StockRowsTable, RequestLinesTable } from '../../components/admin/reservationReset/ResetTables';
import { ConfirmPanel } from '../../components/admin/reservationReset/ConfirmPanel';
import { ResultPanel } from '../../components/admin/reservationReset/ResultPanel';

const errText = (e) => {
  const d = e?.response?.data?.detail;
  return typeof d === 'string' ? d : d?.message || 'Request failed';
};

const useNow = () => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  return now;
};

const Summary = ({ p }) => (
  <div className="grid grid-cols-2 gap-3 md:grid-cols-5" data-testid="reset-summary">
    {[['Reserved to clear', p.reserved_to_clear, 'reserved'], ['Stock rows', p.stock_rows_affected, 'rows'],
      ['Warehouses', p.warehouses_affected, 'warehouses'], ['Request lines → 0', p.request_lines_baselined, 'lines'],
      ['Lines with empty tracking', p.legacy_lines, 'legacy']].map(([label, v, id]) => (
      <div key={id} className="rounded border p-3"><div className="text-xs text-muted-foreground">{label}</div>
        <div className="text-2xl font-semibold" data-testid={`reset-summary-${id}`}>{v}</div></div>
    ))}
  </div>
);

export default function AdminReservationReset() {
  const { tenantId } = useParams();
  const now = useNow();
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [result, setResult] = useState(null);
  const [execError, setExecError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError(''); setExecError('');
    try {
      const { data } = await adminAPI.reservationResetPreview(tenantId);
      setPreview(data);
      if (data.locked) setResult({ reset_id: data.completed_reset_id, reset_at: data.completed_at, locked: true });
    } catch (e) { setError(errText(e)); } finally { setLoading(false); }
  }, [tenantId]);
  useEffect(() => { load(); }, [load]);

  const verify = async (resetId = result?.reset_id) => {
    setVerifying(true);
    try { const { data } = await adminAPI.reservationResetVerify(tenantId, resetId); setResult((r) => ({ ...r, ...data })); }
    catch (e) { toast.error(errText(e)); } finally { setVerifying(false); }
  };
  useEffect(() => { if (result?.locked && !result.checks) verify(result.reset_id); }, [result]); // eslint-disable-line react-hooks/exhaustive-deps

  const execute = async ({ name, reason }) => {
    setRunning(true);
    try {
      const { data } = await adminAPI.reservationResetExecute(tenantId, {
        confirm_tenant_name: name, confirmed: true, preview_token: preview.preview_token, reason,
        expected_reserved_total: preview.reserved_to_clear, expected_stock_rows: preview.stock_rows_affected,
      });
      setResult(data); toast.success('Reset complete');
    } catch (e) { setExecError(errText(e)); toast.error(errText(e)); } finally { setRunning(false); }
  };

  const expired = preview?.preview_expires_at && now > Date.parse(preview.preview_expires_at);
  const secsLeft = preview?.preview_expires_at ? Math.max(0, Math.round((Date.parse(preview.preview_expires_at) - now) / 1000)) : 0;

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6" data-testid="admin-reservation-reset-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold"><ShieldAlert className="h-6 w-6 text-red-600" />Reservation reset</h1>
          <p className="text-sm text-muted-foreground">Primary admins only. Preview first; nothing changes until you confirm.</p>
        </div>
        {!result && <Button variant="outline" onClick={load} disabled={loading} data-testid="reset-refresh-preview">
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />Fresh preview</Button>}
      </div>
      {error && <div className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700" data-testid="reset-error">{error}</div>}
      {preview && (
        <div className="space-y-5">
          <div className="rounded-lg border p-4" data-testid="reset-company">
            <div className="text-lg font-semibold" data-testid="reset-company-name">{preview.tenant_name}</div>
            <div className="font-mono text-xs text-muted-foreground">{preview.tenant_id}</div>
            {!result && <div className="mt-1 text-xs" data-testid="reset-preview-countdown">
              {expired ? 'Preview expired' : `Preview valid for ${Math.floor(secsLeft / 60)}:${String(secsLeft % 60).padStart(2, '0')}`}</div>}
          </div>
          {result ? <ResultPanel tenantId={tenantId} result={result} onVerify={() => verify()} verifying={verifying} /> : (
            <>
              <Summary p={preview} />
              <TotalsTable before={preview.totals} after={preview.totals_after} />
              <h2 className="text-base font-semibold">Stock rows ({preview.rows.length})</h2>
              <StockRowsTable rows={preview.rows} />
              <h2 className="text-base font-semibold">Request lines ({preview.lines.length})</h2>
              <RequestLinesTable lines={preview.lines} />
              <ConfirmPanel preview={preview} expired={expired} running={running} stale={!!execError} error={execError} onExecute={execute} />
            </>
          )}
        </div>
      )}
    </div>
  );
}
